import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentProcessing, ShippingConfig } from 'src/app/_models/configuration-widgets/business-config';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { CustomersService } from 'src/app/_services/customers.service';
import { GlobalService } from 'src/app/_services/global.service';
import { SupportService } from 'src/app/_services/support.service';

@Component({
  selector: 'app-payment-processing',
  templateUrl: './payment-processing.component.html',
  styleUrls: ['./payment-processing.component.scss']
})
export class PaymentProcessingComponent implements OnInit, OnDestroy {

  _global = GlobalService
  
  // Payment Processing Status Properties
  isStripeConnected: boolean = false;
  isOnlinePaymentsConfigured: boolean = false;
  isOnlineStoreSetup: boolean = false;
  documentsReady: boolean = false;
  isSupportContacted: boolean = false;
  isMerchantFormSubmitted: boolean = false;
  isAccountApproved: boolean = false;
  isInStoreSetupComplete: boolean = false;
  
  transactionModeList : any[] =[
    {id:1, value:"test", viewValue:"Test mode"},
    {id:2, value:"live", viewValue:"Live mode"},
  ]
  
  selectedPaymentMethod:any = null 

  isConnectStripeAccountLoading: boolean = false;
  isConnectStripeAccountSuccess: boolean = false;
  isConnectStripeAccountError: boolean = false;
  isConnectStripeAccountErrorText: string = "";

  stripAccountStatus: any = null;
  fetchingStripeStatus: boolean = false;
  
  /**
   * Shows support option and marks support as contacted
   * This is for manual support contact (when user clicks "Contact Support" button)
   */
  ShowSupportOption(): void {
    this._global.ShowHelpSidebar = true;
    //this.isSupportContacted = true; // Mark support as contacted
    
    // Show message about manual support contact
    this._connMessageService.showToastMessage(
      'Support sidebar opened. You can also contact our team directly for immediate assistance.',
      'info'
    );
  }
  
  // Methods for handling status updates
  markDocumentsReady(): void {
    this.documentsReady = true;
    
    // Automatically send Customer service request when documents are ready
    this.sendPaymentProcessingSupportTicket();
    
    // Save status to localStorage
    this.savePaymentProcessingStatus();
    
    // Display success message
    this._connMessageService.showToastMessage(
      'A Customer service request has been automatically created, and our team will contact you for further process.',
      'success'
    );
    
    // Refresh status from API after action
    this.refreshStatusAfterAction();
  }
  
  /**
   * Automatically sends a Customer service request for payment processing setup
   * when documents are marked as ready
   */
  private sendPaymentProcessingSupportTicket(): void {
    const businessInfo = this.getBusinessInformation();
    
    const ticketSubject = 'In-Store Payment Processing Setup - Documents Ready';
    const ticketMessage = this.generateSupportTicketMessage(businessInfo);
    
    // Send the Customer service request using the specialized payment processing method
    this._supportService.sendPaymentProcessingSetupRequest(
      ticketSubject,
      ticketMessage,
      {
        showSuccessMessage: false, // We'll show our own message
        successMessage: ''
      }
    ).subscribe({
      next: (response) => {
        if (response.success) {
          // Automatically mark step 2 as complete since ticket was created
          this.isSupportContacted = true;
          
          // Save status to localStorage
          this.savePaymentProcessingStatus();
          
          // Show additional success message about ticket creation
          setTimeout(() => {
            this._connMessageService.showToastMessage(
              'Customer service request has been created successfully. You will receive an email confirmation shortly.',
              'info'
            );
          }, 1500);
        }
      },
      error: (error) => {
        console.error('Error creating Customer service request:', error);
        this._connMessageService.showToastMessage(
          'There was an issue creating the Customer service request. Please contact support manually.',
          'warning'
        );
      }
    });
  }
  
  /**
   * Generates a comprehensive Customer service request message with business information
   */
  private generateSupportTicketMessage(businessInfo: any): string {
    return `<p>Hello LincSell Team,</p>

<p>I am ready to set up <strong>in-store payment processing</strong> for my business and have all required documents prepared.</p>

<hr />

<h5>BUSINESS INFORMATION:</h5>
<ul>
  <li><strong>Company Name:</strong> ${businessInfo.companyName}</li>
  <li><strong>Business Type:</strong> ${businessInfo.businessType || 'Not specified'}</li>
  
  <li><strong>Contact Email:</strong> ${businessInfo.email}</li>
  <li><strong>Phone:</strong> ${businessInfo.phone || 'Not provided'}</li>
  <li><strong>Business Address:</strong> ${businessInfo.address || 'Not provided'}</li>
</ul>

<hr />

<h5>SETUP REQUIREMENTS:</h5>
<p>I have the following documents ready:</p>
<ul>
  <li>Business Registration Documents</li>
  <li>Banking Information (account details)</li>
  <li>Government-Issued ID (driver’s license or passport)</li>
  <li>Void Cheque or Direct Deposit Form</li>
  <li>Proof of Business Address (utility bill or lease)</li>
</ul>

<hr />

<h5>NEXT STEPS REQUESTED:</h5>
<ol>
  <li>Merchant account setup guidance</li>
  <li>Payment terminal configuration</li>
  <li>Processing rates discussion</li>
  <li>Integration assistance</li>
</ol>

<hr />

<p>Please contact me at your earliest convenience to schedule a consultation and guide me through the payment processing setup.</p>

<p>Thank you for your assistance!</p>

<p><em>This ticket was automatically generated when the user marked their documents as ready in the payment processing setup wizard.</em></p>
`;
  }
  
  /**
   * Gathers business information for the Customer service request
   */
  private getBusinessInformation(): any {
    try {
      const userInfo = this._global.userInfo;
      const businessSetup = userInfo?.businessSetup?.[0];
      
      return {
        companyName: userInfo?.companyName || 'Company name not available',
        email: userInfo?.email || 'Email not available',
        businessType: businessSetup?.businessType || '',
        industry: businessSetup?.industry || '',
        phone: businessSetup?.phone || '',
        address: this.formatBusinessAddress(businessSetup)
      };
    } catch (error) {
      console.warn('Error getting business information:', error);
      return {
        companyName: 'Information not available',
        email: 'Email not available',
        businessType: '',
        industry: '',
        phone: '',
        address: ''
      };
    }
  }
  
  /**
   * Formats the business address from the business setup data
   */
  private formatBusinessAddress(businessSetup: any): string {
    if (!businessSetup) return '';
    
    const addressParts = [
      businessSetup.address,
      businessSetup.city,
      businessSetup.state,
      businessSetup.zip
    ].filter(part => part && part.trim() !== '');
    
    return addressParts.join(', ') || 'Address not provided';
  }
  
  markAccountSetup(): void {
    this.isAccountApproved = true;
    this.savePaymentProcessingStatus();
  }
  
  /**
   * Marks the merchant account as approved
   * This would typically be called by admin or through an API callback
   */
  markAccountApproved(): void {
    this.isAccountApproved = true;
    this.savePaymentProcessingStatus();
    
    this._connMessageService.showToastMessage(
      'Congratulations! Your merchant account has been approved. You can now proceed with terminal setup.',
      'success'
    );
    
    // Refresh status from API after action
    this.refreshStatusAfterAction();
  }
  
  /**
   * Opens the merchant application form in a new tab for online completion
   */
  fillFormOnline(): void {
    const formUrl = 'https://keap.app/contact-us/8007027562205468';
    window.open(formUrl, '_blank');
    
    this._connMessageService.showToastMessage(
      'Merchant application form opened in new tab. Please complete the form online. The system will automatically detect when you have finished.',
      'info'
    );
    
    // Refresh status from API after action
    this.refreshStatusAfterAction();
  }
  
  /**
   * Simulates backend detection of form completion
   * In a real implementation, this would be called by a backend service/webhook
   * when the form is submitted through the external link
   */
  markFormCompleted(): void {
    this.isMerchantFormSubmitted = true;
    this.savePaymentProcessingStatus();
    
    this._connMessageService.showToastMessage(
      'Form completion detected! Your merchant application has been submitted successfully.',
      'success'
    );
    
    // Refresh status from API after action
    this.refreshStatusAfterAction();
  }
  
  /**
   * Schedules terminal setup with customer service
   */
  scheduleTerminalSetup(): void {
    // Send support request for terminal setup scheduling
    this._supportService.sendExpertConsultationRequest(
      'Terminal Setup & Configuration Request',
      `Hello Support Team,

My merchant account has been approved and I'm ready to configure payment terminals for in-store transactions.

BUSINESS INFORMATION:
- Company: ${this.getBusinessInformation().companyName}
- Email: ${this.getBusinessInformation().email}
- Status: Merchant Account Approved

REQUESTED SERVICES:
- Payment terminal configuration
- Test transaction setup
- Staff training on terminal usage
- Integration with POS system

Please contact me to schedule a convenient time for terminal setup and configuration.

Thank you!`,
      'Terminal Configuration',
      {
        showSuccessMessage: true,
        successMessage: 'Terminal setup request sent! Our team will contact you to schedule the configuration.'
      }
    ).subscribe({
      next: (response) => {
        if (response.success) {
          // Optional: Set a flag to track that terminal setup was requested
          // Refresh status from API after action
          this.refreshStatusAfterAction();
        }
      },
      error: (error) => {
        console.error('Error sending terminal setup request:', error);
      }
    });
  }
  
  testPayment(): void {
    // Logic to test online payment - could open a test environment or modal
    this.isOnlineStoreSetup = true;
    this._connMessageService.showToastMessage(`Online payment test completed successfully!`, 'success');
  }
  
  testInStorePayment(): void {
    // Logic to test in-store payment
    this.isInStoreSetupComplete = true;
    this._connMessageService.showToastMessage(`In-store payment test completed successfully!`, 'success');
    
    // Refresh status from API after action
    this.refreshStatusAfterAction();
  }
  
  paymentProcessingForm: FormGroup;
  paymentProcessingData: PaymentProcessing;
  @Input() singupBusinessConfig: boolean = true;
    @Output() widgetSaved = new EventEmitter();
  constructor(private _fb: FormBuilder,
              public _businessSetupService: BusinessSetupService,
              private _connMessageService: ConnMessageService,
              private _abd: AppBaseDataService,
              private _route: ActivatedRoute,
              private _router: Router,
              private _supportService: SupportService
            ) { }

  ngOnInit(): void {
    this.getPaymentProcessing();
    this.initForm()
    this.customeValidation(this.f.transactionMode.value)
    this.f.transactionMode.valueChanges.subscribe(value=> this.customeValidation(value))
    this.loadPaymentProcessingStatus(); // Load saved status
    this.initializeStatusProperties();
    this.fetchPaymentProcessingStatus(); // Fetch current status from API
    this.setupVisibilityListener(); // Listen for tab visibility changes
    this.checkStripeAccountStatus(); // Initial check of Stripe account status
  }
  
  ngOnDestroy(): void {
    // Clean up visibility listener
    if (typeof window !== 'undefined') {
      window.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }
  }
  
  /**
   * Sets up listener for when user returns to the tab
   * This helps detect external form submissions
   */
  private setupVisibilityListener(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }
  }
  
  /**
   * Handles tab visibility changes
   * Refreshes status when user returns to tab (helpful for external form detection)
   */
  private handleVisibilityChange(): void {
    if (!document.hidden) {
      // User returned to tab, refresh status
      console.log('Tab became visible, refreshing payment processing status...');
      this.fetchPaymentProcessingStatus();
    }
  }
  
  // Initialize status properties based on existing data
  initializeStatusProperties(): void {
    // Check if Stripe is connected based on existing payment processing data
    if (this.paymentProcessingData) {
      this.isStripeConnected = !!(this.paymentProcessingData.publishableKey && this.paymentProcessingData.secretKey);
      this.isOnlinePaymentsConfigured = this.isStripeConnected && !!this.paymentProcessingData.transactionMode;
      this.isOnlineStoreSetup = this.isOnlinePaymentsConfigured; // This could be enhanced with additional checks
    }
    
    // These would typically be set based on backend data or user interactions
    // For now, setting them to false as defaults
    this.documentsReady = false;
    this.isSupportContacted = false;
    this.isMerchantFormSubmitted = false;
    this.isAccountApproved = false;
    this.isInStoreSetupComplete = false;
  }
  customeValidation(value:any) {
      if(value=='test'){
        this.f.testPublishableKey.setValidators([Validators.required,Validators.pattern(/^pk_test_[a-zA-Z0-9]+$/)]);
        this.f.testSecretKey.setValidators([Validators.required,Validators.pattern(/^sk_test_[a-zA-Z0-9]+$/)]);
        this.f.livePublishableKey.clearValidators();
        this.f.liveSecretKey.clearValidators();
      }else{
        this.f.livePublishableKey.setValidators([Validators.required,Validators.pattern(/^pk_live_[a-zA-Z0-9]+$/)]);
        this.f.liveSecretKey.setValidators([Validators.required,Validators.pattern(/^sk_live_[a-zA-Z0-9]+$/)]);
        this.f.testPublishableKey.clearValidators();
        this.f.testSecretKey.clearValidators();
      }
      this.f.testPublishableKey.updateValueAndValidity();
      this.f.testSecretKey.updateValueAndValidity();
      this.f.livePublishableKey.updateValueAndValidity();
      this.f.liveSecretKey.updateValueAndValidity();
    
    

     this.selectedPaymentMethod = value
  }

  
  initForm() {
    this.paymentProcessingForm = this._fb.group({
      transactionMode: [""],
      testPublishableKey: [""],
      testSecretKey: [""],
      livePublishableKey: [""],
      liveSecretKey: [""],
     })
  }
  get f() {
    return this.paymentProcessingForm.controls;
  }
  set f(values: any) {
    if (this.paymentProcessingForm) {
      this.paymentProcessingForm.setValue(values);
    }
  }

  getPaymentProcessing(){
    try{
      this._abd.showSpinner()
      this._businessSetupService.getPaymentProcessing().subscribe(res=>{
        if(res.success){
          this.paymentProcessingData = res.data
         this.setValues(this.paymentProcessingData)
         this.initializeStatusProperties(); // Update status after data is loaded
          this._abd.hideSpinner()
        }
      })
    }
    catch{
      this._abd.hideSpinner()
    }
  }
  goTo(value){
     if(value === 1){
      window.open('https://dashboard.stripe.com/login?redirect=%2Fapikeys', '_blank');
    }else{
       window.open('https://dashboard.stripe.com/register', '_blank');
     }
  }
  setValues(values:any) {
    if(this.selectedPaymentMethod && this.selectedPaymentMethod !== null){
      values.transactionMode  = this.selectedPaymentMethod
    }
    this.f = {
      transactionMode:values.transactionMode,
      testPublishableKey:values.testPublishableKey.trim(),
      testSecretKey:values.testSecretKey.trim(),
      livePublishableKey:values.livePublishableKey.trim(),
      liveSecretKey:values.liveSecretKey.trim(),
    }
}
  saveFn(){
    let paymentProcessingValue: any = {
      transactionMode: this.f.transactionMode.value,
      testPublishableKey: this.f.testPublishableKey.value, 
      livePublishableKey: this.f.livePublishableKey.value, 
      testSecretKey: this.f.testSecretKey.value, 
      liveSecretKey: this.f.liveSecretKey.value, 
      isSaved:true,
    }
    try{
      this._abd.showSpinner()
      this._businessSetupService.updatePaymentProcessing(this.paymentProcessingData.id,paymentProcessingValue).subscribe(res=>{
        if(res.success){
          // this._abd.systemConfigs.paymentProcessing.isSaved = true
            this.goToRoute()
          this._connMessageService.showToastMessage(`Payment Processing saved successfully!`, 'success')
          this.getPaymentProcessing()
        }else{
          this._connMessageService.showToastMessage(`Error saving Payment Processing!`, 'error')
          this._abd.hideSpinner()
        }
      })
    }
    catch{
      this._connMessageService.showToastMessage(`Error saving Payment Processing!`, 'error')
      this._abd.hideSpinner()
    }
  }
  goToRoute() {
    let page = null;
    let route = null
  
    this._route.queryParams.subscribe(params => {
      page = params['page']; 
    });
    if(page !== undefined){
      if(page.includes('payment-processing')){
      //  this._router.navigate([""])
      }
    }
  }
  cancelButtonClick(){
    this._businessSetupService.showPrintingConfiguration = false
  }

  // Add persistence for payment processing status
  private readonly PAYMENT_STATUS_KEY = 'payment_processing_status';
  
  /**
   * Fetches the current payment processing status from the API
   * This should be called on page load and after any action that could affect status
   */
  private fetchPaymentProcessingStatus(): void {
    try {
      this._abd.showSpinner();
      this._businessSetupService.getPaymentProcessingStatus().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.updateStatusFromAPI(response.data);
          }
          this._abd.hideSpinner();
        },
        error: (error) => {
          console.error('Error fetching payment processing status:', error);
          this._abd.hideSpinner();
          // Fallback to localStorage if API fails
          this.loadPaymentProcessingStatus();
        }
      });
    } catch (error) {
      console.error('Error in fetchPaymentProcessingStatus:', error);
      this._abd.hideSpinner();
      // Fallback to localStorage if API fails
      this.loadPaymentProcessingStatus();
    }
  }
  
  /**
   * Updates the component status based on API response
   */
  private updateStatusFromAPI(statusData: any): void {
    this.documentsReady = statusData.documentsReady || false;
    this.isSupportContacted = statusData.isSupportContacted || false;
    this.isMerchantFormSubmitted = statusData.isMerchantFormSubmitted || false;
    this.isAccountApproved = statusData.isAccountApproved || false;
    this.isInStoreSetupComplete = statusData.isInStoreSetupComplete || false;
    
    // Save to localStorage as backup
    this.savePaymentProcessingStatus();
    
    // Log the current status for debugging
    console.log('Payment processing status updated from API:', {
      documentsReady: this.documentsReady,
      isSupportContacted: this.isSupportContacted,
      isMerchantFormSubmitted: this.isMerchantFormSubmitted,
      isAccountApproved: this.isAccountApproved,
      isInStoreSetupComplete: this.isInStoreSetupComplete
    });
  }
  
  /**
   * Manually refreshes the payment processing status
   * Can be called by user action or button click
   */
  refreshStatus(): void {
    this._connMessageService.showToastMessage(
      'Refreshing payment processing status...',
      'info'
    );
    this.fetchPaymentProcessingStatus();
  }
  
  /**
   * Refreshes the payment processing status after an action
   * Call this after any action that could affect the status
   */
  private refreshStatusAfterAction(): void {
    // Small delay to allow backend processing
    setTimeout(() => {
      this.fetchPaymentProcessingStatus();
    }, 1000);
  }
  
  /**
   * Saves the current payment processing status to localStorage
   */
  private savePaymentProcessingStatus(): void {
    const status = {
      isStripeConnected: this.isStripeConnected,
      isOnlinePaymentsConfigured: this.isOnlinePaymentsConfigured,
      isOnlineStoreSetup: this.isOnlineStoreSetup,
      documentsReady: this.documentsReady,
      isSupportContacted: this.isSupportContacted,
      isMerchantFormSubmitted: this.isMerchantFormSubmitted,
      isAccountApproved: this.isAccountApproved,
      isInStoreSetupComplete: this.isInStoreSetupComplete,
      lastUpdated: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(this.PAYMENT_STATUS_KEY, JSON.stringify(status));
    } catch (error) {
      console.warn('Could not save payment processing status to localStorage:', error);
    }
  }
  
  /**
   * Loads the payment processing status from localStorage
   */
  private loadPaymentProcessingStatus(): void {
    try {
      const savedStatus = localStorage.getItem(this.PAYMENT_STATUS_KEY);
      if (savedStatus) {
        const status = JSON.parse(savedStatus);
        
        // Only load saved status if it's from today (to prevent stale data)
        const lastUpdated = new Date(status.lastUpdated);
        const today = new Date();
        const isFromToday = lastUpdated.toDateString() === today.toDateString();
        
        if (isFromToday) {
          this.documentsReady = status.documentsReady || false;
          this.isSupportContacted = status.isSupportContacted || false;
          this.isMerchantFormSubmitted = status.isMerchantFormSubmitted || false;
          this.isAccountApproved = status.isAccountApproved || false;
          this.isInStoreSetupComplete = status.isInStoreSetupComplete || false;
        }
      }
    } catch (error) {
      console.warn('Could not load payment processing status from localStorage:', error);
    }
  }

  get WebsiteLink(){
    let websiteDomain: string = '';
    websiteDomain = window.location.origin + '/'; // this._globals.whiteLabel.domain
    var n = websiteDomain.lastIndexOf('/');
    var result = websiteDomain.substring(n + 1);

    if (result == '') {
        websiteDomain = websiteDomain.substring(
            0,
            websiteDomain.length - 1
        );
    }
    return websiteDomain;
  }

  checkStripeAccountStatus() {
    this.fetchingStripeStatus = true;
    this._businessSetupService.CheckConnectAccount().subscribe({
      next: (response) => {
        if (response.success) {
          this.isStripeConnected = response.data.chargesEnabled || false;
          this.stripAccountStatus = response.data.status;
        }
      },
      error: (error) => {
        console.error('Error checking Stripe account status:', error);
        // this._connMessageService.showToastMessage(
        //   'There was an issue checking the Stripe account status. Please try again later.',
        //   'warning'
        // );
      },
      complete: () => {
        this.fetchingStripeStatus = false;
      }
    });
  }

  openConnectStripeAccount() {
    this.isConnectStripeAccountLoading = true;
    const UserURL = this._global.webStoreConfiguration?.webStoreHost;
    const accountConfig = {
      "Email": this._global.userInfo.username,
      "ReturnUrl": this.WebsiteLink+"/success",
      "RefreshUrl": this.WebsiteLink+"/reauth",
      "UserId": this._global.userInfo.businessSetup[0].userID,
    }
    this._businessSetupService.SetupConnectAccount(accountConfig).subscribe({
      next: (response) => {
        
        if (response.data.onboardingUrl != undefined && response.data.onboardingUrl != null) {
          window.open(response.data.onboardingUrl, '_blank');
          this.isConnectStripeAccountSuccess = true;
          this.isConnectStripeAccountError = false;
          this.isConnectStripeAccountErrorText = '';
        } else {
          this.isConnectStripeAccountError = true;
          this.isConnectStripeAccountErrorText = response.message || 'Failed to initiate Stripe account connection.';
        }
      },
      error: (error) => {
        console.log('Error connecting to Stripe:', error);
        this.isConnectStripeAccountError = true;
         this.isConnectStripeAccountLoading = false;
        this.isConnectStripeAccountErrorText = 'An error occurred while connecting to Stripe. Please try again later.';
      },
      complete: () => {
        this.isConnectStripeAccountLoading = false;
      }
    });
  }

}